<template>
    <!--  -->
    <nav class="bg-white w-full z-10 relative">
        <div class="max-w-page mx-auto w-full h-auto relative z-10">
            <div class="grid grid-cols-2 lg:grid-cols-5 px-6 lg:px-4 z-10 relative">
                <NuxtLink no-prefetch to="/leads" class="group h-16 lg:h-24 flex items-center cursor-pointer order-1"
                    @mouseover="$refs.lottie.play()"
                    v-if="!adminOverride"
                >
                    <div class="lg:w-10 lg:h-10 w-7 h-7">
                        <ClientOnly>
                            <Vue3Lottie
                                ref="lottie"
                                :animationData="ZapoLogoJson"
                                height="100%"
                                width="100%"
                                :playOnHover="false"
                                :autoPlay="false"
                                :loop="true"
                                @onLoopComplete="$refs?.lottie?.stop()"
                            />
                        </ClientOnly>
                    </div>

                    <IconZapoLogoWhiteNoIcon class="h-6 lg:h-8 order-1 mt-[7px] ml-1 fill-black" />
                </NuxtLink>

                <div class="order-3 lg:order-2 col-span-3 relative"
                    :class='{
                        "!col-span-5": adminOverride,
                    }'
                >
                    <div class="hidden lg:flex items-center justify-center gap-8 z-10 h-24 relative" v-if="$route.path != '/signup/complete'">
                        <component
                            :to="link.link"
                            class="flex"
                            v-for="(link, li) in links"
                            :key="li"
                            :disabled="link.disabled"
                            :class="{
                                'cursor-pointer': !link.disabled && !link.active,
                                'cursor-default': link.disabled || link.active,
                            }"
                            :is="link.disabled || adminOverride ? 'div' : 'router-link'"
                            @click='() => $event.$emit("sign-up-nav-click", li+1)'
                        >
                            <div
                                class="w-6 h-6 rounded-full text-12 font-bold text-center leading-6"
                                :class="{
                                    'bg-green-500 text-white': link.active,
                                    'bg-grey-400 text-grey-800': !link.active,
                                    'bg-grey-400 text-black cursor-pointer': !link.active && !link.disabled,
                                }"
                            >
                                {{ li + 1 }}
                            </div>

                            <p class="ml-2 text-15">{{ link.name }}</p>
                        </component>
                    </div>

                    <Select
                        class="block lg:hidden relative"
                        :class="{
                            'opacity-0': userMenu,
                        }"
                        :options="links"
                        valueKey="link"
                        :value="$route.path"
                        menuClass="bg-white"
                        boxClass="bg-white relative"
                        @update:value="
                            (path) => {
                                $router.push(path)
                            }
                        "
                        v-if="$route.path != '/signup/complete'"
                    >
                        <template #option="{ option, selected }">
                            <div class="flex relative">
                                <div
                                    class="w-6 h-6 rounded-full text-12 font-bold text-center leading-6 relative"
                                    :class="{
                                        'bg-green-500 text-white': option.active,
                                        'bg-grey-400 text-grey-800': !option.active,
                                        'bg-grey-400 text-black cursor-pointer': !option.active && option.click,
                                    }"
                                >
                                    {{ option.i }}
                                </div>

                                <p class="ml-2 text-15 font-normal">{{ option.name }}</p>
                            </div>
                        </template>
                    </Select>
                </div>

                <div class="flex items-center justify-end order-2 lg:order-3" v-if="!adminOverride">
                    <div class="relative overflow-hidden lg:overflow-visible">
                        <button class="button-black h-12 w-[100px] font-bold lg:block hidden" @click="help = !help">Help</button>

                        <div
                            class="border border-grey-500 absolute top-18 p-6 w-[324px] -right-[102px] transition-all ease-in-out duration-300 transform-gpu"
                            :class="{
                                'opacity-0': !help,
                            }"
                        >
                            <div class="flex">
                                <component :is="liveChatEnabled ? 'IconPersonGreen' : 'IconPersonRed'" class="h-7 w-7 shrink-0 cursor-pointer mr-3" />
                                <div>
                                    <p class="text-17 font-hero" v-if="liveChatEnabled">We’re open</p>
                                    <p class="text-17 font-hero" v-if="!liveChatEnabled">We’re closed</p>
                                    <p class="text-14 mt-1.5">If you need help, our friendly team are here to answer your questions.</p>
                                </div>
                            </div>

                            <button
                                class="button-black w-full h-12 mt-6"
                                @click="
                                    () => {
                                        this.$event.$emit('intercom:show')
                                        this.help = false
                                    }
                                "
                            >
                                {{ liveChatEnabled ? 'Chat now' : 'Leave a message' }}
                            </button>
                        </div>
                    </div>

                    <DropdownV2
                        class="shrink-0"
                        :hasShadow="false"
                        id="user-menu"
                        forceSide="right"
                        :menuSpace="24"
                        menuClass="left-0 lg:left-[unset]"
                        @dropdown="
                            (open) => {
                                if (open && help) {
                                    help = false
                                }

                                userMenu = !userMenu
                            }
                        "
                    >
                        <template #button="{ open }">
                            <div class="ml-6 relative hidden w-12 h-12 bg-grey-300 rounded-tl-lg rounded-br-lg lg:flex items-center justify-center">
                                <IconUser class="h-6 w-6 stroke-grey-700" />
                            </div>

                            <IconMenu class="block lg:hidden w-8 h-8 stroke-black" v-if="!open" />
                            <IconClose class="block lg:hidden w-6 h-6 stroke-black" v-if="open" />

                            <IconArrowDown :class="{ 'rotate-180': open }" class="hidden lg:block h-4.5 w-4.5 ml-3 transition-all stroke-black" />
                        </template>

                        <template #menu>
                            <div class="lg:border border-grey-500 pt-5 p-6 w-full lg:w-[324px] bg-white">
                                <div class="lg:hidden">
                                    <div class="flex">
                                        <component :is="liveChatEnabled ? 'IconPersonGreen' : 'IconPersonRed'" class="h-7 w-7 shrink-0 cursor-pointer mr-3" />

                                        <div class="w-full">
                                            <p class="text-17 font-hero" v-if="liveChatEnabled">We’re open</p>
                                            <p class="text-17 font-hero" v-if="!liveChatEnabled">We’re closed</p>
                                            <p class="text-14 mt-1.5">If you need help, our friendly team are here to answer your questions.</p>
                                        </div>
                                    </div>

                                    <button
                                        class="button-black w-full h-12 mt-6"
                                        data-close
                                        @click="
                                            () => {
                                                this.$event.$emit('intercom:show')
                                                this.help = false
                                            }
                                        "
                                    >
                                        {{ liveChatEnabled ? 'Chat now' : 'Leave a message' }}
                                    </button>
                                </div>

                                <div class="flex items-center w-full mt-6 lg:mt-0">
                                    <div class="mr-6 relative lg:hidden w-12 h-12 bg-grey-300 rounded-tl-lg rounded-br-lg flex items-center justify-center shrink-0">
                                        <IconUser class="h-6 w-6 stroke-grey-700" />
                                    </div>

                                    <div class="w-full">
                                        <div class="flex justify-between items-center">
                                            <p class="text-19 lg:font-sub capitalize">Hi {{ user.first_name }}!</p>

                                            <button @click="logout" class="hover:opacity-50 cursor-pointer">
                                                <IconPower class="h-5 w-5 stroke-black cursor-pointer" />
                                            </button>
                                        </div>

                                        <p class="text-grey-900 text-15">{{ user.email }}</p>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </DropdownV2>
                </div>
            </div>

            <transition-fade>
                <div class="fixed inset-0 bg-black/80 top-16 transition-all duration-300 ease-in-out lg:hidden" v-if="userMenu" />
            </transition-fade>
        </div>
    </nav>
</template>

<script>
import ZapoLogoJson from '~/assets/animation-zapo-logo.json'

export default {
    props: {
        adminOverride: { type: Boolean, default: false },
        user: { type: Object, default: null },
        adminCompany: { type: Object, default: null },
    },

    mounted() {},

    data() {
        return {
            ZapoLogoJson,
            help: false,
            userMenu: false,
        }
    },

    methods: {
        async logout() {
            await this.$authSanctum.logout()
        },
    },

    computed: {
        // user() {
        //     return this.$authSanctum.getUser()
        // },

        company() {
            if (this.adminCompany) {
                return this.adminCompany
            }

            return this.user.loadSubscription().company
        },

        liveChatEnabled() {
            return this.$storeGet('liveChatEnabled')
        },

        links() {
            console.log(this.company, this.adminCompany);
            if (this.user.currentView() == 'sign-up') {
                return [
                    {
                        name: 'Company',
                        link: '/signup/company',
                        active: this.$route.path == '/signup/company',
                        disabled: this.user.sign_up_max_step < 1,
                        i: 1,
                    },
                    {
                        name: 'Product',
                        link: '/signup/product',
                        active: this.$route.path == '/signup/product',
                        disabled: this.user.sign_up_max_step < 2,
                        i: 2,
                    },
                    {
                        name: 'Plan',
                        link: '/signup/plan',
                        active: this.$route.path == '/signup/plan',
                        disabled: this.user.sign_up_max_step < 3,
                        i: 3,
                    },
                    {
                        name: 'Payment',
                        link: '/signup/payment',
                        active: this.$route.path == '/signup/payment',
                        disabled: this.user.sign_up_max_step < 4,
                        i: 4,
                    },
                ]
            } else {
                return [
                    {
                        name: 'Business',
                        link: '/onboarding/business',
                        active: this.$route.path == '/onboarding/business',
                        click: this.company.on_boarding_max_step >= 1,
                        disabled: this.company.on_boarding_max_step < 1,
                        i: 1,
                    },
                    {
                        name: 'Images',
                        link: '/onboarding/images',
                        active: this.$route.path == '/onboarding/images',
                        click: this.company.on_boarding_max_step >= 2,
                        disabled: this.company.on_boarding_max_step < 2,
                        i: 2,
                    },
                    {
                        name: 'Verification',
                        link: '/onboarding/verification',
                        active: this.$route.path == '/onboarding/verification',
                        click: this.company.on_boarding_max_step >= 3,
                        disabled: this.company.on_boarding_max_step < 3,
                        i: 3,
                    },
                    {
                        name: 'Landing Page',
                        link: '/onboarding/landing-page',
                        active: this.$route.path == '/onboarding/landing-page',
                        click: this.company.on_boarding_max_step >= 4,
                        disabled: this.company.on_boarding_max_step < 4,
                        i: 4,
                    },
                    {
                        name: 'Launch',
                        link: '/onboarding/launch',
                        active: this.$route.path == '/onboarding/launch',
                        click: this.company.on_boarding_max_step >= 5,
                        disabled: this.company.on_boarding_max_step < 5,
                        i: 5,
                    },
                ]
            }
        },
    },
}
</script>
